import { useState, useEffect, useCallback, useMemo, startTransition } from 'react'
import { TranslationInput } from '@/types/common'
import { useQueryClient } from '@tanstack/react-query'
import { setCookie, getClientCookie } from '@/utils'
import { useCinematicsStore } from '@/cinematics/stores/CinematicsStore'
import { merge } from '@/utils/nunchee-kit'

import { es, enUS, ptBR } from 'date-fns/locale'
import globalTranslations from './translations'
import { normalizeLanguage, type SupportedLanguage } from './languages'
import { isIdentifiedUser } from '@/constants/roles'

import settings from '@/settings/public'

/**
 * Inserts dynamic parameters into a translation string
 *
 * @param {string} translation - The translation string with placeholders in {paramName} format
 * @param {Record<string, any>} params - Object containing values to insert into the placeholders
 * @returns {string} The translation with all placeholders replaced by their values
 * @example
 * // Returns "Hello John, you have 3 messages"
 * insertParams("Hello {name}, you have {count} messages", { name: "John", count: 3 })
 */
const insertParams = (translation: string, params: Record<string, any>) => {
  return Object.keys(params).reduce((acc, paramKey) => {
    return acc.replace(new RegExp(`{${paramKey}}`, 'g'), params[paramKey])
  }, translation)
}

/**
 * Gets the correct translation for a key in the specified language
 *
 * @param {any} key - Translation key or object containing translations
 * @param {SupportedLanguage} lang - The target language code
 * @param {Record<string, any>} [localTranslations] - Optional component-specific translations
 * @returns {string} The translated string in the requested language
 *
 * @remarks
 * - If key is an object, returns key[lang], key.original, or key.en (in that order)
 * - If key is a string, looks it up in merged global and local translations
 * - Falls back to the original key if no translation is found
 */
const handleTranslation = (key: any, lang: SupportedLanguage, localTranslations?: Record<string, any>) => {
  if (typeof key !== 'string') return key?.[lang] || key?.original || key?.en

  const translations = merge(globalTranslations, localTranslations)
  const libraryTranslation =
    (translations?.[key as keyof typeof translations] as { en: string; es: string; pt: string } | undefined)?.[lang]
  return libraryTranslation || key
}

/**
 * Custom hook for translations functionality
 *
 * @param {Object} [props] - Hook configuration options
 * @param {Record<string, any>} [props.localTranslations] - Component-specific translations to merge with global ones
 * @returns {Object} Translation utilities and language information
 * @returns {Function} t - Function to translate keys with optional parameter interpolation
 * @returns {string} lang - Current active language code
 * @returns {string} locale - User's full locale (e.g., 'en-US')
 * @returns {Function} setLanguage - Function to change the current language
 * @returns {Function} hasTranslation - Check if a translation key exists
 * @returns {Object} dateLocale - date-fns locale object for the current language
 *
 * @example
 * // Basic usage
 * const { t } = useTranslation()
 * return <p>{t('Welcome')}</p>
 *
 * @example
 * // With parameters
 * const { t } = useTranslation()
 * return <p>{t('Hello {name}', { name: 'John' })}</p>
 *
 * @example
 * // With component-specific translations
 * const { t } = useTranslation({ localTranslations: myComponentTranslations })
 * return <p>{t('component_specific_key')}</p>
 */
// Set this to a language code to override the language for development purposes
// e.g. 'es' for Spanish, 'pt' for Portuguese, or null to disable the override
const DEV_LANGUAGE_OVERRIDE: SupportedLanguage | null = normalizeLanguage(process.env.NEXT_PUBLIC_LANGUAGE_OVERRIDE)

export function useTranslation(props?: { localTranslations?: Record<string, any> }) {
  const { localTranslations = {} } = props || {}
  const { app } = settings
  const queryClient = useQueryClient()
  const profile: any = queryClient.getQueryData(['profile'])
  const { initialLanguage } = useCinematicsStore()
  const [currentLang, setCurrentLang] = useState<SupportedLanguage>(
    DEV_LANGUAGE_OVERRIDE || normalizeLanguage(initialLanguage) || 'en'
  )
  const [userLocale, setUserLocale] = useState<string>('en-US')

  useEffect(() => {
    // If DEV_LANGUAGE_OVERRIDE is set, use it and skip the rest of the logic
    if (DEV_LANGUAGE_OVERRIDE) {
      startTransition(() => setCurrentLang(DEV_LANGUAGE_OVERRIDE))
      return
    }

    const cookieLang = normalizeLanguage(initialLanguage)
    const isClientSide = typeof window !== 'undefined'

    // Set user locale from navigator if available
    if (isClientSide) {
      startTransition(() => setUserLocale(navigator.language || 'en-US'))
    }

    // Fallback to navigator language if cookie is not set
    const fallBackLang = cookieLang
      || (isClientSide ? normalizeLanguage(window.navigator.language) : null)
      || normalizeLanguage(app?.defaultLanguage)
      || 'en'
    const isLoggedIn = isIdentifiedUser(profile?.rbac?.role)
    // A logged-in profile with a missing/unsupported `language` keeps the fallback:
    // an empty value used to be persisted to the cookie and then fed to Intl APIs.
    const selectedLang = (isLoggedIn ? normalizeLanguage(profile?.language) : null) || fallBackLang

    startTransition(() => setCurrentLang(selectedLang))

    // Compare the raw cookie, not `cookieLang`: `initialLanguage` is already
    // normalized server-side, so a legacy malformed cookie (`es expires=Fri, …`,
    // which carries no real expiry and no `path=/`) would look equal here and
    // never get rewritten in the fixed format.
    if (isClientSide && getClientCookie(`_${app?.code}:lang`) !== selectedLang) {
      setCookie(`_${app?.code}:lang`, selectedLang, 365)
    }
  }, [profile?.language, profile?.rbac?.role, initialLanguage, app?.code, app?.defaultLanguage])

  /**
   * Translates a key to the current language with optional parameter substitution
   *
   * @param {TranslationInput | undefined} key - Translation key or object with translations
   * @param {Record<string, any>} [params={}] - Parameters to insert into the translation
   * @returns {string} Translated text with parameters inserted
   */
  const t = useCallback((key: TranslationInput | undefined, params: Record<string, any> = {}) => {
    if (!key) return ''
    const translation = handleTranslation(key, currentLang, localTranslations)
    return params ? insertParams(translation, params) : translation
  }, [currentLang, localTranslations])

  /**
   * Checks if a translation exists for the given key
   *
   * @param {TranslationInput} key - Translation key to check
   * @returns {boolean} True if the translation exists, false otherwise
   */
  const hasTranslation = useCallback((key: TranslationInput) => {
    return !!handleTranslation(key, currentLang, localTranslations)
  }, [currentLang, localTranslations])

  /**
   * Updates the current language and stores it in a cookie
   *
   * @param {SupportedLanguage} newLang - The language code to switch to
   */
  const setLanguage = (newLang: SupportedLanguage) => {
    const lang = normalizeLanguage(newLang)
    if (!lang) return

    setCurrentLang(lang)
    setCookie(`_${app?.code}:lang`, lang, 365) // Update cookie
  }

  /**
   * Returns the date-fns locale object for the current language
   * Used for date formatting with date-fns
   */
  const dateLocale = useMemo(() => {
    return {
      en: enUS,
      es: es,
      pt: ptBR,
    }[currentLang] || enUS
  }, [currentLang])

  return {
    t,
    lang: currentLang,
    locale: userLocale,
    setLanguage,
    hasTranslation,
    dateLocale,
  }
}